import { DocumentCategory, DocumentTypes } from "@/prisma-package/src";

/**
 * Single source of truth for how documents are bucketed into the three tabs.
 *
 * `category` (DocumentCategory) is the primary discriminator — it names the tab
 * a document belongs to. `type` is a DocumentTypeDefinition key: a finer
 * sub-classification within a category. Every type maps to exactly one
 * category, except OTHER, which is genuinely ambiguous and is resolved by
 * ownership scope.
 *
 * The DocumentTypeDefinition registry is the authority on each type's
 * category and label, so every helper accepts the registry definitions and
 * prefers them. The static maps below are a fallback for keys the provided
 * definitions don't cover (a deactivated type still on old documents, a
 * failed registry load, callers with no definitions at hand): legacy standard
 * keys resolve to their historical buckets, anything else to vendor
 * compliance — the historical invariant.
 */

// Minimal registry-definition shape the enumeration/label helpers accept
// (structurally compatible with DocumentTypeDefinitionRecord).
export type DocTypeDefLike = {
  key: string;
  label: string;
  category: DocumentCategory;
  active?: boolean;
};

// Fallback buckets for the legacy standard keys, used only when the provided
// definitions don't cover a key. OTHER appears in two buckets because its
// category depends on scope — see categoryForType.
const FALLBACK_TYPES_BY_CATEGORY: Record<DocumentCategory, string[]> = {
  [DocumentCategory.PROJECT_EXPENSE]: [
    DocumentTypes.INVOICE,
    DocumentTypes.RECEIPT,
    DocumentTypes.PROOF_OF_PAYMENT,
    DocumentTypes.OTHER,
  ],
  [DocumentCategory.VENDOR_COMPLIANCE]: [
    DocumentTypes.W9,
    DocumentTypes.BUSINESS_LICENSE,
    DocumentTypes.OTHER,
  ],
  [DocumentCategory.CHECKLIST]: [DocumentTypes.CHECKLIST_DOCUMENT],
};

const FALLBACK_CATEGORY_BY_TYPE: Record<string, DocumentCategory> =
  Object.fromEntries(
    (
      Object.entries(FALLBACK_TYPES_BY_CATEGORY) as [
        DocumentCategory,
        string[],
      ][]
    ).flatMap(([category, types]) =>
      types
        .filter((type) => type !== DocumentTypes.OTHER)
        .map((type) => [type, category]),
    ),
  );

// Look up a key's category in the registry definitions, when provided.
const registryCategory = (
  type: string,
  defs?: DocTypeDefLike[],
): DocumentCategory | undefined =>
  defs?.find((def) => def.key === type)?.category;

/**
 * Resolve which category a document belongs to from its type.
 *
 * OTHER is the only ambiguous type: an OTHER document with a projectId is a
 * project expense; otherwise it is treated as vendor compliance (org-shared).
 * Everything else resolves through the provided registry definitions; a key
 * not found there falls back to its legacy bucket, then vendor compliance.
 */
export const categoryForType = (
  type: string,
  opts?: { hasProjectId?: boolean; defs?: DocTypeDefLike[] },
): DocumentCategory => {
  if (type === DocumentTypes.OTHER) {
    return opts?.hasProjectId
      ? DocumentCategory.PROJECT_EXPENSE
      : DocumentCategory.VENDOR_COMPLIANCE;
  }
  return (
    registryCategory(type, opts?.defs) ??
    FALLBACK_CATEGORY_BY_TYPE[type] ??
    DocumentCategory.VENDOR_COMPLIANCE
  );
};

/**
 * The document types that may appear within a category. Used to build the
 * type-filter buttons and type selects in the documents UI. Driven by the
 * provided registry definitions (active types whose registry category
 * matches); legacy standard keys the definitions don't cover keep their
 * fallback bucket, and OTHER always goes last in the two tabs it is valid in.
 */
export const typesForCategory = (
  category: DocumentCategory,
  defs?: DocTypeDefLike[],
): string[] => {
  const coveredKeys = new Set((defs ?? []).map((def) => def.key));
  const fallbackKeys = FALLBACK_TYPES_BY_CATEGORY[category].filter(
    (type) => type !== DocumentTypes.OTHER && !coveredKeys.has(type),
  );
  const registryKeys = (defs ?? [])
    .filter(
      (def) =>
        def.category === category &&
        def.active !== false &&
        def.key !== DocumentTypes.OTHER,
    )
    .map((def) => def.key);
  const hasOther = FALLBACK_TYPES_BY_CATEGORY[category].includes(
    DocumentTypes.OTHER,
  );
  return [
    ...fallbackKeys,
    ...registryKeys,
    ...(hasOther ? [DocumentTypes.OTHER as string] : []),
  ];
};

/**
 * Whether a document type is valid within a category. OTHER is valid in both
 * non-checklist tabs; everything else resolves like categoryForType.
 */
export const typeBelongsToCategory = (
  type: string,
  category: DocumentCategory,
  defs?: DocTypeDefLike[],
): boolean =>
  type === DocumentTypes.OTHER
    ? category !== DocumentCategory.CHECKLIST
    : categoryForType(type, { defs }) === category;

// The type to fall back to when a detected type does not fit a category.
const DEFAULT_TYPE_BY_CATEGORY: Record<DocumentCategory, string> = {
  [DocumentCategory.PROJECT_EXPENSE]: DocumentTypes.OTHER,
  [DocumentCategory.VENDOR_COMPLIANCE]: DocumentTypes.OTHER,
  [DocumentCategory.CHECKLIST]: DocumentTypes.CHECKLIST_DOCUMENT,
};

/**
 * The type a category falls back to when detection produces something that does
 * not belong to it.
 */
export const defaultTypeForCategory = (category: DocumentCategory): string =>
  DEFAULT_TYPE_BY_CATEGORY[category];

/**
 * Constrain a (possibly auto-detected) type to a known category. When the type
 * does not belong to the category — e.g. an invoice detected while uploading in
 * the Vendor Compliance tab — it falls back to that category's default type so
 * the stored type and category stay consistent.
 */
export const constrainTypeToCategory = (
  type: string,
  category: DocumentCategory,
  defs?: DocTypeDefLike[],
): string =>
  typeBelongsToCategory(type, category, defs)
    ? type
    : defaultTypeForCategory(category);

/**
 * Advisory check for "uploaded to the wrong tab". Given a detected type and the
 * category (tab) the user uploaded into, returns the category the document
 * likely belongs in — or null when there is nothing to warn about.
 *
 * Warning-only: this never changes how a document is stored. It exists so the
 * UI can nudge the user when, e.g., an INVOICE lands in the Vendor Compliance
 * tab. OTHER is deliberately never flagged (it is genuinely ambiguous and valid
 * in more than one tab), so a low-confidence/unclassified upload stays quiet.
 */
export const wrongTabSuggestion = (
  detectedType: string,
  category: DocumentCategory,
  opts?: { hasProjectId?: boolean; defs?: DocTypeDefLike[] },
): DocumentCategory | null => {
  if (detectedType === DocumentTypes.OTHER) return null;
  if (typeBelongsToCategory(detectedType, category, opts?.defs)) return null;
  return categoryForType(detectedType, opts);
};

/**
 * Resolve the category an upload should be SAVED to, letting the detected type
 * route the document instead of forcing the tab it was uploaded from: a W9
 * uploaded from the Project Expenses tab goes to Vendor Compliance. The tab
 * still wins when the type belongs to it (including OTHER, which is valid in
 * more than one tab), and Checklist uploads are always deliberate so they are
 * never rerouted. With no tab category the legacy type/scope resolution in
 * resolveDocumentPlacement applies unchanged.
 */
export const resolveUploadCategory = (
  detectedType: string,
  uploadCategory: DocumentCategory | undefined,
  opts?: { hasProjectId?: boolean; defs?: DocTypeDefLike[] },
): DocumentCategory | undefined => {
  if (!uploadCategory) return undefined;
  if (uploadCategory === DocumentCategory.CHECKLIST) return uploadCategory;
  return wrongTabSuggestion(detectedType, uploadCategory, opts) ?? uploadCategory;
};

/**
 * Resolve where a document should live from a (possibly auto-detected) type, an
 * optional explicit category (the tab it was uploaded into), and whether it is
 * project-scoped. This is the single source of truth used by both the create
 * action and the pre-save extraction step so they always agree.
 *
 * - When a category is explicit (the documents tabs), the type is constrained to
 *   it and scope follows the category (vendor compliance is org-shared).
 * - Otherwise the category is derived from the type/scope and legacy scoping is
 *   preserved (org-scoped only when there is no project).
 */
export const resolveDocumentPlacement = (input: {
  type: string;
  category?: DocumentCategory;
  hasProjectId: boolean;
  defs?: DocTypeDefLike[];
}): {
  type: string;
  category: DocumentCategory;
  isOrgScoped: boolean;
} => {
  const hasExplicitCategory = !!input.category;
  const category =
    input.category ??
    categoryForType(input.type, {
      hasProjectId: input.hasProjectId,
      defs: input.defs,
    });
  const type = hasExplicitCategory
    ? constrainTypeToCategory(input.type, category, input.defs)
    : input.type;
  const isOrgScoped = hasExplicitCategory
    ? category === DocumentCategory.VENDOR_COMPLIANCE
    : !input.hasProjectId;
  return { type, category, isOrgScoped };
};

/**
 * Human-friendly name for a category, used in the documents tabs and warnings.
 */
export const categoryLabel = (category: DocumentCategory): string => {
  switch (category) {
    case DocumentCategory.PROJECT_EXPENSE:
      return "Project Expenses";
    case DocumentCategory.VENDOR_COMPLIANCE:
      return "Vendor Compliance";
    case DocumentCategory.CHECKLIST:
      return "Checklist";
  }
};

/**
 * Human-friendly name for a document type, with an article, for use in prose
 * warnings (e.g. "This looks like an invoice"). Pass registry definitions so
 * custom types can be named from their label.
 */
export const documentTypeLabel = (
  type: string,
  defs?: DocTypeDefLike[],
): string => {
  if (type === DocumentTypes.OTHER) return "a document";
  const label = defs?.find((def) => def.key === type)?.label;
  if (label) {
    const article = /^[aeiou]/i.test(label) ? "an" : "a";
    return `${article} ${label}`;
  }
  // Legacy prose fallbacks for keys the provided definitions don't cover.
  switch (type) {
    case DocumentTypes.INVOICE:
      return "an invoice";
    case DocumentTypes.RECEIPT:
      return "a receipt";
    case DocumentTypes.PROOF_OF_PAYMENT:
      return "a proof of payment";
    case DocumentTypes.W9:
      return "a W-9";
    case DocumentTypes.BUSINESS_LICENSE:
      return "a business license";
    case DocumentTypes.CHECKLIST_DOCUMENT:
      return "a checklist document";
    default:
      return "a document";
  }
};
